// src/contexts/CompanyContext.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
  CompanyService,
  CompanyProfile,
  CompanyLocation,
  CompanySocialMedia,
} from '../Services/companyService';

interface CompanyContextType {
  companyProfile: CompanyProfile | null;
  loading: boolean;
  error: string | null;
  refreshCompanyProfile: () => Promise<void>;
  updateCompanyProfile: (profile: CompanyProfile) => Promise<void>;
  createCompanyProfile: (profile: CompanyProfile) => Promise<void>;
  hasCompanyProfile: boolean;
  currencySymbol: string;
  taxPercentage: number;
  location: CompanyLocation;
  socialMedia: CompanySocialMedia;
  formatPrice: (price: number) => string;
  calculateTax: (subtotal: number) => number;
  calculateTotalWithTax: (subtotal: number) => number;
}

const defaultLocation: CompanyLocation = {
  address: '',
  latitude: 0,
  longitude: 0,
};

const defaultSocialMedia: CompanySocialMedia = {
  facebook: '',
  instagram: '',
  threads: '',
};

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

export const useCompany = () => {
  const context = useContext(CompanyContext);
  if (context === undefined) {
    throw new Error('useCompany must be used within a CompanyProvider');
  }
  return context;
};

interface CompanyProviderProps {
  children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({ children }) => {
  const [companyProfile, setCompanyProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasCompanyProfile, setHasCompanyProfile] = useState(false);

  const fetchCompanyProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const profile = await CompanyService.getCompanyProfile();

      if (profile) {
        setCompanyProfile(profile);
        setHasCompanyProfile(true);
      } else {
        setCompanyProfile(null);
        setHasCompanyProfile(false);
      }
    } catch (err: any) {
      console.error('Error fetching company profile:', err);
      setError(err.message || 'Failed to load company profile');
      setCompanyProfile(null);
      setHasCompanyProfile(false);
    } finally {
      setLoading(false);
    }
  };

  const refreshCompanyProfile = async () => {
    await fetchCompanyProfile();
  };

  const updateCompanyProfile = async (profile: CompanyProfile) => {
    try {
      setError(null);
      await CompanyService.updateCompanyProfile(profile);
      await fetchCompanyProfile();
    } catch (err: any) {
      console.error('Error updating company profile:', err);
      setError(err.message || 'Failed to update company profile');
      throw err;
    }
  };

  const createCompanyProfile = async (profile: CompanyProfile) => {
    try {
      setError(null);
      await CompanyService.createCompanyProfile(profile);
      await fetchCompanyProfile();
    } catch (err: any) {
      console.error('Error creating company profile:', err);
      setError(err.message || 'Failed to create company profile');
      throw err;
    }
  };

  const getCurrencySymbol = (): string => {
    if (!companyProfile || !companyProfile.currency) return '$';
    return companyProfile.currency;
  };

  const getTaxPercentage = (): number => {
    if (!companyProfile) return 0;
    return companyProfile.taxPercentage || 0;
  };

  const getLocation = (): CompanyLocation => {
    if (!companyProfile || !companyProfile.location) return defaultLocation;
    return companyProfile.location;
  };

  const getSocialMedia = (): CompanySocialMedia => {
    if (!companyProfile || !companyProfile.socialMedia) return defaultSocialMedia;
    return companyProfile.socialMedia;
  };

const formatPrice = (price: number): string => {
  const symbol = getCurrencySymbol();
  const formatted = price.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${symbol} ${formatted}`;
};

  const calculateTax = (subtotal: number): number => {
    return CompanyService.calculateTax(subtotal, getTaxPercentage());
  };

  const calculateTotalWithTax = (subtotal: number): number => {
    return CompanyService.calculateTotalWithTax(subtotal, getTaxPercentage());
  };

  useEffect(() => {
    fetchCompanyProfile();
  }, []);

  const value: CompanyContextType = {
    companyProfile,
    loading,
    error,
    refreshCompanyProfile,
    updateCompanyProfile,
    createCompanyProfile,
    hasCompanyProfile,
    currencySymbol: getCurrencySymbol(),
    taxPercentage: getTaxPercentage(),
    location: getLocation(),
    socialMedia: getSocialMedia(),
    formatPrice,
    calculateTax,
    calculateTotalWithTax,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
};